import { ProductService } from '@/view/pages/products/ProductService';
import { BrandService } from '@/view/pages/brand/services/BrandService';
import { unMaskPrice } from '@/utils/common';
import { PRODUCT_MUTATIONS } from './mutationTypes';

const productModule = {
  namespaced: true,

  state: {
    // Lists data
    loading: false,
    error: null,
    categories: [],
    filteredCategories: [],
    brands: [],
    filteredBrands: [],
    colors: [],
    sizes: [],
    products: [],
    filteredProducts: [],
    productHistory: [],
    productHistoryLoading: false,

    // Selected values
    selectedCategoryId: null,
    selectedBrandId: null,
    selectedParentId: null,
    selectedOriginalId: null,

    // Product data
    productInfo: {
      name: '',
      code: '',
      barcode: '',
      selectedParentId: null,
      selectedCate: '',
      selectedType: 1,
      selectedStatus: 1,
      importPrice: 0,
      salePrice: 0,
      wholesalePrice: 0,
      listedPrice: 0,
      warrantyMonthNo: 0,
      productVat: null,
      productCodeVat: '',
      productNameVat: '',
      productTradeName: '',
      productBrandCode: '',
      alias: '',
      sku: '',
      unitId: null,
      searchBrandName: '',
      note: '',
      imageIds: [],
      productRelationType: '',
    },

    productOptions: [],
    childProducts: [],
    comboProducts: [],

    // Product assets
    productAssets: {},
  },

  mutations: {
    [PRODUCT_MUTATIONS.SET_LOADING](state, loading) {
      state.loading = loading;
    },

    [PRODUCT_MUTATIONS.SET_PRODUCT_INFO](state, productInfo) {
      state.productInfo = productInfo;
    },

    [PRODUCT_MUTATIONS.SET_ERROR](state, error) {
      state.error = error;
    },

    [PRODUCT_MUTATIONS.SET_PRODUCT_HISTORY](state, history) {
      state.productHistory = history;
    },

    [PRODUCT_MUTATIONS.SET_PRODUCT_HISTORY_LOADING](state, loading) {
      state.productHistoryLoading = loading;
    },

    [PRODUCT_MUTATIONS.SET_CATEGORIES](state, categories) {
      state.categories = categories;
      state.filteredCategories = [...categories];
    },

    [PRODUCT_MUTATIONS.SET_FILTERED_CATEGORIES](state, categories) {
      state.filteredCategories = categories;
    },

    [PRODUCT_MUTATIONS.SET_BRANDS](state, brands) {
      state.brands = brands;
      state.filteredBrands = [...brands];
    },

    [PRODUCT_MUTATIONS.SET_FILTERED_BRANDS](state, brands) {
      state.filteredBrands = brands;
    },

    [PRODUCT_MUTATIONS.SET_COLORS](state, colors) {
      state.colors = colors;
    },

    [PRODUCT_MUTATIONS.SET_SIZES](state, sizes) {
      state.sizes = sizes;
    },

    [PRODUCT_MUTATIONS.SET_PRODUCTS](state, products) {
      state.products = products;
      state.filteredProducts = [...products];
    },

    [PRODUCT_MUTATIONS.SET_FILTERED_PRODUCTS](state, products) {
      state.filteredProducts = products;
    },

    [PRODUCT_MUTATIONS.SET_SELECTED_CATEGORY_ID](state, id) {
      state.selectedCategoryId = id;
    },

    [PRODUCT_MUTATIONS.SET_SELECTED_BRAND_ID](state, id) {
      state.selectedBrandId = id;
    },

    [PRODUCT_MUTATIONS.SET_SELECTED_PARENT_ID](state, id) {
      state.selectedParentId = id;
    },

    [PRODUCT_MUTATIONS.SET_SELECTED_ORIGINAL_ID](state, id) {
      state.selectedOriginalId = id;
    },

    [PRODUCT_MUTATIONS.UPDATE_PRODUCT_INFO](state, info) {
      state.productInfo = { ...state.productInfo, ...info };
    },

    [PRODUCT_MUTATIONS.SET_PRODUCT_OPTIONS](state, options) {
      state.productOptions = options;
    },

    [PRODUCT_MUTATIONS.SET_CHILD_PRODUCTS](state, products) {
      state.childProducts = products;
    },

    [PRODUCT_MUTATIONS.ADD_CHILD_PRODUCT](state, product) {
      state.childProducts.push(product);
    },

    [PRODUCT_MUTATIONS.UPDATE_CHILD_PRODUCT](state, { id, updates }) {
      const index = state.childProducts.findIndex((p) => p.id === id);
      if (index !== -1) {
        Object.assign(state.childProducts[index], updates);
      }
    },

    [PRODUCT_MUTATIONS.REMOVE_CHILD_PRODUCT](state, id) {
      state.childProducts = state.childProducts.filter((p) => p.id !== id);
    },

    [PRODUCT_MUTATIONS.SET_COMBO_PRODUCTS](state, products) {
      state.comboProducts = products;
    },

    [PRODUCT_MUTATIONS.ADD_COMBO_PRODUCT](state, product) {
      state.comboProducts.push(product);
    },

    [PRODUCT_MUTATIONS.UPDATE_COMBO_PRODUCT](state, { index, updates }) {
      if (index >= 0 && index < state.comboProducts.length) {
        state.comboProducts[index] = {
          ...state.comboProducts[index],
          ...updates,
        };
      }
    },

    [PRODUCT_MUTATIONS.REMOVE_COMBO_PRODUCT](state, id) {
      state.comboProducts = state.comboProducts.filter((p) => p.id !== id);
    },

    [PRODUCT_MUTATIONS.TOGGLE_COMBO_PRODUCT_EDIT](state, index) {
      if (index >= 0 && index < state.comboProducts.length) {
        state.comboProducts[index].isEdit = !state.comboProducts[index].isEdit;
      }
    },
  },

  actions: {
    resetProductInfo({ commit }) {
      commit(PRODUCT_MUTATIONS.SET_PRODUCT_INFO, {
        id: null,
        name: '',
        code: '',
        barcode: '',
        selectedParentId: null,
        selectedCate: '',
        selectedType: 1,
        selectedStatus: 1,
        importPrice: 0,
        salePrice: 0,
        wholesalePrice: 0,
        listedPrice: 0,
        warrantyMonthNo: 0,
        productVat: null,
        productCodeVat: '',
        productNameVat: '',
        productTradeName: '',
        productBrandCode: '',
        alias: '',
        sku: '',
        unitId: null,
        searchBrandName: '',
        note: '',
        imageIds: [],
      });

      commit(PRODUCT_MUTATIONS.SET_PRODUCT_OPTIONS, []);
      commit(PRODUCT_MUTATIONS.SET_CHILD_PRODUCTS, []);
      commit(PRODUCT_MUTATIONS.SET_COMBO_PRODUCTS, []);
    },

    async addImagesToProduct({ commit }, { productId, imageIds }) {
      commit(PRODUCT_MUTATIONS.SET_LOADING, true);
      try {
        const response = await ProductService.addImagesToProduct(
          productId,
          imageIds,
        );
        if (response.data.success) {
          commit(PRODUCT_MUTATIONS.UPDATE_PRODUCT_INFO, response.data.product);
        }
        return response.data.product;
      } catch (error) {
        console.error(`Error adding images to product ${productId}:`, error);
        throw error;
      } finally {
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
      }
    },

    async removeImagesFromProduct({ commit }, { productId, imageIds }) {
      commit(PRODUCT_MUTATIONS.SET_LOADING, true);
      try {
        const response = await ProductService.removeImagesFromProduct(
          productId,
          imageIds,
        );
        if (response.data.success) {
          commit(PRODUCT_MUTATIONS.UPDATE_PRODUCT_INFO, response.data.product);
        }
        return response.data.product;
      } catch (error) {
        console.error(
          `Error removing images from product ${productId}:`,
          error,
        );
        throw error;
      } finally {
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
      }
    },

    async getProductAssets({ commit }, productId) {
      try {
        const response = await ProductService.getProductAssets(productId);
        commit(PRODUCT_MUTATIONS.SET_PRODUCT_ASSETS, {
          productId,
          assets: response.data.assets || [],
        });
        return response.data.assets;
      } catch (error) {
        console.error(`Error fetching product assets ${productId}:`, error);
        throw error;
      }
    },

    fetchProductById: async ({ commit }, productId) => {
      try {
        commit(PRODUCT_MUTATIONS.SET_LOADING, true);
        commit(PRODUCT_MUTATIONS.SET_ERROR, null);
        const response = await ProductService.getProductById(productId);

        if (response.status === 1 && response.data) {
          const productData = response.data;

          commit(PRODUCT_MUTATIONS.SET_PRODUCT_INFO, {
            id: productData.id,
            name: productData.productName,
            code: productData.productCode,
            barCode: productData.barCode,
            listedPrice: productData.listedPrice,
            originalPrice: productData.originalPrice,
            salePrice: productData.sellingPrice,
            wholesalePrice: productData.wholesalePrice,
            selectedStatus: productData.status,
            selectedType: productData.productType,
            unitId: productData.unitId,
            productVat: productData.productVat,
            selectedCate: productData.categoryName,
            searchBrandName: productData.brandName,
            selectedParent: productData.parentProductName,
            imageIds: productData.imageIds,
            importPrice: productData.originalPrice,
            productRelationType: productData.productRelationType,
            warrantyMonthNo: productData.warrantyMonthNo,
            productCodeVat: productData.productCodeVat,
            productNameVat: productData.productNameVat,
            productBrandCode: productData.productBrandCode,
            alias: productData.alias || '',
            sku: productData.sku || '',
            note: productData.note,
          });

          // Cập nhật các ID tham chiếu
          commit(
            PRODUCT_MUTATIONS.SET_SELECTED_CATEGORY_ID,
            productData.productCategory,
          );
          commit(PRODUCT_MUTATIONS.SET_SELECTED_BRAND_ID, productData.brand);
          commit(
            PRODUCT_MUTATIONS.SET_SELECTED_PARENT_ID,
            productData.parentProductId,
          );

          // Lấy danh sách sản phẩm con nếu có
          if (
            productData.childProducts &&
            productData.childProducts.length > 0
          ) {
            commit(
              PRODUCT_MUTATIONS.SET_CHILD_PRODUCTS,
              productData.childProducts,
            );
          }

          // Lấy danh sách sản phẩm combo nếu có
          if (
            productData.comboProducts &&
            productData.comboProducts.length > 0
          ) {
            commit(
              PRODUCT_MUTATIONS.SET_COMBO_PRODUCTS,
              productData.comboProducts,
            );
          }
          // Lấy danh sách options nếu có
          if (
            productData.productOptions &&
            productData.productOptions.length > 0
          ) {
            const productOptions = productData.productOptions.map((option) => ({
              ...option,
              optionValues: option.optionValues.map((opt) => opt.value),
            }));

            commit(PRODUCT_MUTATIONS.SET_PRODUCT_OPTIONS, productOptions);
          }

          return productData;
        } else {
          commit(
            PRODUCT_MUTATIONS.SET_ERROR,
            'Không tìm thấy thông tin sản phẩm',
          );
          return null;
        }
      } catch (error) {
        commit(
          PRODUCT_MUTATIONS.SET_ERROR,
          error.message || 'Lỗi khi lấy thông tin sản phẩm',
        );
        return null;
      } finally {
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
      }
    },

    updateProduct: async ({ commit, state, getters }, { id, form }) => {
      try {
        commit(PRODUCT_MUTATIONS.SET_LOADING, true);
        commit(PRODUCT_MUTATIONS.SET_ERROR, null);

        const productInfo = getters.getProductInfo;

        const productData = {
          id: id,
          productName: form.name,
          productCode: form.code,
          barCode: productInfo.barCode,
          listedPrice: parseFloat(
            productInfo.listedPrice.toString().replace(/,/g, ''),
          ),
          originalPrice: parseFloat(
            productInfo.originalPrice.toString().replace(/,/g, ''),
          ),
          sellingPrice: parseFloat(
            productInfo.salePrice.toString().replace(/,/g, ''),
          ),
          wholesalePrice: parseFloat(
            productInfo.wholesalePrice.toString().replace(/,/g, ''),
          ),
          importPrice: parseFloat(
            productInfo.importPrice.toString().replace(/,/g, ''),
          ),
          status: productInfo.selectedStatus,
          productType: productInfo.selectedType,
          unitId: form.unitId,
          productVat: form.productVat,
          productCategory: state.selectedCategoryId,
          brand: state.selectedBrandId,
          parentProductId: state.selectedParentId,
          childProducts: state.childProducts,
          comboProducts: state.comboProducts,
          productOptions: state.productOptions,
          imageIds: productInfo.imageIds,
          warrantyMonthNo: form.warrantyMonthNo,
          productCodeVat: form.productCodeVat,
          productNameVat: form.productNameVat,
          productBrandCode: form.productBrandCode,
          alias: form.alias || productInfo.alias || '',
          sku: form.sku || productInfo.sku || '',
          note: form.note,
        };

        const response = await ProductService.updateProduct(productData);

        if (response.status === 1) {
          return response;
        } else {
          commit(
            PRODUCT_MUTATIONS.SET_ERROR,
            response.message || 'Lỗi khi cập nhật sản phẩm',
          );
          return response;
        }
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.message ||
          'Lỗi khi cập nhật sản phẩm';
        commit(PRODUCT_MUTATIONS.SET_ERROR, message);
        return { status: 0, message };
      } finally {
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
      }
    },

    /**
     * Initialize product data
     */
    async initProductData({ dispatch }) {
      try {
        await Promise.all([
          dispatch('fetchCategories'),
          dispatch('fetchBrands'),
          dispatch('fetchColors'),
          dispatch('fetchSizes'),
        ]);
        return true;
      } catch (error) {
        console.error('Error initializing product data:', error);
        return false;
      }
    },

    /**
     * Fetch categories
     */
    async fetchCategories({ commit }) {
      try {
        commit(PRODUCT_MUTATIONS.SET_LOADING, true);
        const categories = await ProductService.getCategories();
        commit(PRODUCT_MUTATIONS.SET_CATEGORIES, categories);
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        return categories;
      } catch (error) {
        commit(
          PRODUCT_MUTATIONS.SET_ERROR,
          error.message || 'Failed to fetch categories',
        );
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        throw error;
      }
    },

    /**
     * Filter categories by name
     */
    filterCategories({ commit, state }, searchText) {
      if (!searchText) {
        commit(PRODUCT_MUTATIONS.SET_FILTERED_CATEGORIES, state.categories);
        return;
      }

      const filteredCategories = ProductService.filterCategories(
        state.categories,
        searchText,
      );
      commit(PRODUCT_MUTATIONS.SET_FILTERED_CATEGORIES, filteredCategories);
    },

    /**
     * Fetch brands
     */
    async fetchBrands({ commit }) {
      try {
        commit(PRODUCT_MUTATIONS.SET_LOADING, true);
        const brands = await ProductService.getBrands();
        commit(PRODUCT_MUTATIONS.SET_BRANDS, brands);
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        return brands;
      } catch (error) {
        commit(
          PRODUCT_MUTATIONS.SET_ERROR,
          error.message || 'Failed to fetch brands',
        );
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        throw error;
      }
    },

    /**
     * Filter brands by name
     */
    filterBrands({ commit, state }, searchText) {
      if (!searchText) {
        commit(PRODUCT_MUTATIONS.SET_FILTERED_BRANDS, state.brands);
        return;
      }

      const filteredBrands = ProductService.filterBrands(
        state.brands,
        searchText,
      );
      commit(PRODUCT_MUTATIONS.SET_FILTERED_BRANDS, filteredBrands);
    },

    /**
     * Create a new brand
     */
    async createBrand({ commit, dispatch }, brandData) {
      try {
        commit(PRODUCT_MUTATIONS.SET_LOADING, true);
        const result = await BrandService.createBrand(brandData.name);
        await dispatch('fetchBrands');
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        return result;
      } catch (error) {
        commit(
          PRODUCT_MUTATIONS.SET_ERROR,
          error.message || 'Failed to create brand',
        );
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        throw error;
      }
    },

    /**
     * Fetch color properties
     */
    async fetchColors({ commit }) {
      try {
        commit(PRODUCT_MUTATIONS.SET_LOADING, true);
        const colors = await ProductService.getPropertiesByType(1);
        commit(PRODUCT_MUTATIONS.SET_COLORS, colors);
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        return colors;
      } catch (error) {
        commit(
          PRODUCT_MUTATIONS.SET_ERROR,
          error.message || 'Failed to fetch colors',
        );
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        throw error;
      }
    },

    /**
     * Fetch size properties
     */
    async fetchSizes({ commit }) {
      try {
        commit(PRODUCT_MUTATIONS.SET_LOADING, true);
        const sizes = await ProductService.getPropertiesByType(2);
        commit(PRODUCT_MUTATIONS.SET_SIZES, sizes);
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        return sizes;
      } catch (error) {
        commit(
          PRODUCT_MUTATIONS.SET_ERROR,
          error.message || 'Failed to fetch sizes',
        );
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        throw error;
      }
    },

    /**
     * Create a property value
     */
    async createPropertyValue({ commit, dispatch }, { propertyData, typeId }) {
      try {
        commit(PRODUCT_MUTATIONS.SET_LOADING, true);
        const result = await ProductService.createPropertyValue(propertyData);

        // Refresh the relevant property list
        if (typeId === 1) {
          await dispatch('fetchColors');
        } else if (typeId === 2) {
          await dispatch('fetchSizes');
        }

        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        return result;
      } catch (error) {
        commit(
          PRODUCT_MUTATIONS.SET_ERROR,
          error.message || 'Failed to create property value',
        );
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        throw error;
      }
    },

    /**
     * Search for products
     */
    async searchProducts({ commit }, searchText) {
      try {
        commit(PRODUCT_MUTATIONS.SET_LOADING, true);
        const products = await ProductService.searchProducts(searchText);
        commit(PRODUCT_MUTATIONS.SET_PRODUCTS, products);
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        return products;
      } catch (error) {
        commit(
          PRODUCT_MUTATIONS.SET_ERROR,
          error.message || 'Failed to search products',
        );
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        throw error;
      }
    },

    /**
     * Update product information
     */
    updateProductInfo({ commit }, info) {
      commit(PRODUCT_MUTATIONS.UPDATE_PRODUCT_INFO, info);
    },

    /**
     * Set product options
     */
    setProductOptions({ commit }, options) {
      commit(PRODUCT_MUTATIONS.SET_PRODUCT_OPTIONS, options);
    },

    /**
     * Generate child products based on options
     */
    generateChildProducts({ commit, state }, { isGenChild }) {
      try {
        const childProducts = ProductService.generateChildProducts(
          state.productInfo,
          state.productOptions,
          {
            isGenChild,
            selectedParent: state.productInfo.selectedParent,
            selectedType: state.productInfo.selectedType,
            selectedClassParent: state.selectedParentId,
            selectedBrand: state.selectedBrandId,
            selectedOriginal: state.selectedOriginalId,
            selectedStatus: state.productInfo.selectedStatus,
            form: {
              name: state.productInfo.name,
              code: state.productInfo.code,
              selectedCate: state.productInfo.selectedCate,
              listedPrice: state.productInfo.listedPrice,
            },
          },
        );

        commit(PRODUCT_MUTATIONS.SET_CHILD_PRODUCTS, childProducts);
        return childProducts;
      } catch (error) {
        console.error('Error generating child products:', error);
        return [];
      }
    },

    /**
     * Add a child product
     */
    addChildProduct({ commit }, childProduct) {
      commit(PRODUCT_MUTATIONS.ADD_CHILD_PRODUCT, childProduct);
    },

    /**
     * Update a child product
     */
    updateChildProduct({ commit }, { id, updates }) {
      commit(PRODUCT_MUTATIONS.UPDATE_CHILD_PRODUCT, { id, updates });
    },

    /**
     * Remove a child product
     */
    removeChildProduct({ commit }, id) {
      commit(PRODUCT_MUTATIONS.REMOVE_CHILD_PRODUCT, id);
    },

    /**
     * Add a combo product
     */
    addComboProduct({ commit }, product) {
      commit(PRODUCT_MUTATIONS.ADD_COMBO_PRODUCT, product);
    },

    /**
     * Update a combo product
     */
    updateComboProduct({ commit }, { index, updates }) {
      commit(PRODUCT_MUTATIONS.UPDATE_COMBO_PRODUCT, { index, updates });
    },

    /**
     * Remove a combo product
     */
    removeComboProduct({ commit }, id) {
      commit(PRODUCT_MUTATIONS.REMOVE_COMBO_PRODUCT, id);
    },

    /**
     * Toggle combo product edit mode
     */
    toggleComboProductEdit({ commit }, index) {
      commit(PRODUCT_MUTATIONS.TOGGLE_COMBO_PRODUCT_EDIT, index);
    },

    /**
     * Calculate sum of combo product prices
     */
    calculateComboSum({ state }) {
      return ProductService.calculateComboSum(state.comboProducts);
    },

    /**
     * Fetch product history
     */
    async fetchProductHistory({ commit }, { productId }) {
      try {
        commit(PRODUCT_MUTATIONS.SET_PRODUCT_HISTORY_LOADING, true);

        const response = await ProductService.fetchProductHistory(productId);

        if (response && response.status === 1) {
          commit(PRODUCT_MUTATIONS.SET_PRODUCT_HISTORY, response.data || []);
        } else {
          commit(PRODUCT_MUTATIONS.SET_PRODUCT_HISTORY, []);
          commit(
            PRODUCT_MUTATIONS.SET_ERROR,
            (response && response.message) || 'Failed to fetch product history',
          );
        }

        commit(PRODUCT_MUTATIONS.SET_PRODUCT_HISTORY_LOADING, false);
        return response;
      } catch (error) {
        console.error('Error in fetchProductHistory:', error);
        commit(PRODUCT_MUTATIONS.SET_PRODUCT_HISTORY_LOADING, false);
        commit(PRODUCT_MUTATIONS.SET_PRODUCT_HISTORY, []);
        commit(
          PRODUCT_MUTATIONS.SET_ERROR,
          error.message || 'Failed to fetch product history',
        );
        throw error;
      }
    },

    /**
     * Save product
     */
    async saveProduct({ state, commit }, { form, uuid }) {
      try {
        commit(PRODUCT_MUTATIONS.SET_LOADING, true);

        // Check if combo product
        if (state.productInfo.selectedType === 3) {
          // Prepare combo product data
          const comboItems = state.comboProducts.map((item) => ({
            ...item,
            productComboPrice: unMaskPrice(item.productComboPrice),
          }));

          const comboData = ProductService.prepareComboProductData(
            state.productInfo,
            form,
            comboItems,
            {
              selectedCateId: state.selectedCategoryId,
              selectedOriginal: state.selectedOriginalId,
              selectedBrand: state.selectedBrandId,
              uuid,
            },
          );

          // Create combo product
          const result = await ProductService.createComboProduct(comboData);
          commit(PRODUCT_MUTATIONS.SET_LOADING, false);
          return result;
        } else {
          // Prepare product data
          const products = [];

          // Main product
          const mainProduct = ProductService.prepareProductData(
            state.productInfo,
            form,
            {
              selectedCateId: state.selectedCategoryId,
              selectedClassParent: state.selectedParentId,
              selectedBrand: state.selectedBrandId,
              selectedOriginal: state.selectedOriginalId,
              uuid,
            },
          );

          products.push(mainProduct);

          // Child products
          if (state.childProducts.length > 0) {
            state.childProducts.forEach((childProduct) => {
              const childData = ProductService.prepareChildProductData(
                mainProduct,
                childProduct,
                {
                  selectedCateId: state.selectedCategoryId,
                  uuid,
                },
              );

              products.push(childData);
            });
          }

          // Create products
          const result = await ProductService.createProductList(products);
          commit(PRODUCT_MUTATIONS.SET_LOADING, false);
          return result;
        }
      } catch (error) {
        commit(
          PRODUCT_MUTATIONS.SET_ERROR,
          error.message || 'Failed to save product',
        );
        commit(PRODUCT_MUTATIONS.SET_LOADING, false);
        throw error;
      }
    },
  },

  getters: {
    getLoading: (state) => state.loading,
    getError: (state) => state.error,
    getProductHistory: (state) => state.productHistory,
    getProductHistoryLoading: (state) => state.productHistoryLoading,
    getCategories: (state) => state.categories,
    getFilteredCategories: (state) => state.filteredCategories,
    getBrands: (state) => state.brands,
    getFilteredBrands: (state) => state.filteredBrands,
    getColors: (state) => state.colors,
    getSizes: (state) => state.sizes,
    getProducts: (state) => state.products,
    getFilteredProducts: (state) => state.filteredProducts,
    getSelectedCategoryId: (state) => state.selectedCategoryId,
    getSelectedBrandId: (state) => state.selectedBrandId,
    getSelectedParentId: (state) => state.selectedParentId,
    getSelectedOriginalId: (state) => state.selectedOriginalId,
    getProductInfo: (state) => state?.productInfo || {},
    getProductOptions: (state) => state.productOptions,
    getChildProducts: (state) => state.childProducts,
    getComboProducts: (state) => state.comboProducts,
    getProductAssetsById: (state) => (productId) =>
      state.productAssets[productId] || [],

    // Computed getters
    getShowCombo: (state) => state.productInfo.selectedType === 3,
  },
};

export default productModule;
